import { useEffect } from "react";
import { useAuthContext } from "providers/Auth";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "untitledui-js-base";
import { Menu } from "./Menu";
import { useProgressContext } from "providers/ProgressContext";
import { Loader } from "shared/components/Loader";
import { CsvUploadStatus } from "providers/ProgressContext/slices/UploadCsvSlice/types";
import { cx } from "shared/utils";
import logoSquare from "shared/branding/logo-square.svg";
import { IconButton, IconButtonVariant } from "shared/components/IconButton";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButtonSize,
  Show
} from "shared/components";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { MasterSearch } from "shared/app/MasterSearch";
import { NavItemId } from "../constants";
import { useNavCustomization } from "../use-nav-customization";
import { CustomizeNavHeader } from "./CustomizeNavHeader";
import { CustomizeNavSections } from "./CustomizeNavSections";
import { ExploreProductsNavButton } from "./ExploreProductsNavButton";
import { HiddenUpdatesNotice } from "./HiddenUpdatesNotice";
import { NavSections } from "./NavSections";
import { NotificationToasts } from "./NotificationToasts";

const SidebarStatusBanner = () => {
  const navigate = useNavigate();
  const { subscriptionsQueue, uploadCsv } = useProgressContext();

  if (subscriptionsQueue.isSyncing)
    return (
      <div className="pl-4 pr-6">
        <div className="flex items-center gap-3 overflow-x-hidden p-3">
          <div className="shrink-0">
            <Loader size={20} />
          </div>
          <p className="text-xs text-content-500">
            Subscriptions queued: {subscriptionsQueue.amount}
          </p>
        </div>
      </div>
    );

  if (uploadCsv.status === CsvUploadStatus.Parsing)
    return (
      <Link to="/rent-reporting/upload">
        <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
          <div className="flex items-center gap-3 overflow-x-hidden p-3">
            <div className="mt-1 shrink-0 self-start">
              <Loader size={20} />
            </div>
            <p className="text-xs text-content-500">
              Analyzing rent reporting CSV file...
            </p>
          </div>
        </div>
      </Link>
    );

  if (uploadCsv.status === CsvUploadStatus.Parsed)
    return (
      <Link to="/rent-reporting/upload">
        <div className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6">
          <div className="flex items-center gap-3 overflow-x-hidden p-3">
            <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
              <Check size="14px" className="stroke-white stroke-[2px]" />
            </div>
            <p className="text-xs text-success-800">
              Rent reporting CSV file analysis ready to review
            </p>
          </div>
        </div>
      </Link>
    );

  if (uploadCsv.status === CsvUploadStatus.Executing)
    return (
      <Link to="/rent-reporting/upload">
        <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
          <div className="flex items-center gap-3 overflow-x-hidden p-3">
            <div className="mt-1 shrink-0 self-start">
              <Loader size={20} />
            </div>
            <p className="text-xs text-content-500">
              Processing rent reporting CSV file...
            </p>
          </div>
        </div>
      </Link>
    );

  if (uploadCsv.status === CsvUploadStatus.Executed)
    return (
      <div
        className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6 cursor-pointer"
        onClick={() => {
          navigate("/rent-reporting/upload", {
            state: {
              review: true
            }
          });
        }}
      >
        <div className="flex items-center gap-3 overflow-x-hidden p-3">
          <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
            <Check size="14px" className="stroke-white stroke-[2px]" />
          </div>
          <p className="text-xs text-success-800">
            Rent reporting CSV file processed successfully
          </p>
        </div>
      </div>
    );

  return null;
};

interface AsideProps {
  isOpenFromMobile: boolean;
  setIsOpenFromMobile: React.Dispatch<React.SetStateAction<boolean>>;
}

const getAsideWidthClassName = ({
  isCustomizing,
  isOpenFromMobile
}: {
  isCustomizing: boolean;
  isOpenFromMobile: boolean;
}) => {
  if (isCustomizing)
    return "w-[272px] left-0 shadow-2xl ring-1 ring-primary-50";
  if (isOpenFromMobile) return "w-[240px] left-0";
  return "w-[80px] left-[-240px] md:left-0 md:hover:w-[240px]";
};

const AsideTop = ({ isOpenFromMobile, setIsOpenFromMobile }: AsideProps) => {
  const { isCustomizing, startCustomizing } = useNavCustomization();

  if (isCustomizing) return <CustomizeNavHeader />;

  if (isOpenFromMobile)
    return (
      <IconButton
        HugeIcon={Cancel01Icon}
        variant={IconButtonVariant.TEXT_BASE}
        size={IconButtonSize.SM}
        className="!text-content-200 my-6 ml-6"
        onClick={() => setIsOpenFromMobile(false)}
      />
    );

  return (
    <div className="relative mb-8 mt-4 flex w-full items-center">
      <Link to="/" className="ml-6 block">
        <img
          src={logoSquare}
          className="h-[30px] w-[30px] hover:rotate-[360deg] transition-all duration-300 ease-in-out rounded-full"
        />
      </Link>
      <div className="invisible absolute inset-y-0 right-4 flex items-center whitespace-nowrap opacity-0 transition-opacity md:group-hover:visible md:group-hover:opacity-100 md:group-hover:delay-150">
        <Button
          variant={ButtonVariant.TEXT}
          size={ButtonSize.XS}
          onClick={startCustomizing}
        >
          Customize menu
        </Button>
      </div>
    </div>
  );
};

const AsideContent = ({
  isOpenFromMobile,
  setIsOpenFromMobile
}: AsideProps) => {
  const { type } = useAuthContext();
  const { sections, isCustomizing, stopCustomizing } = useNavCustomization();
  const hasNotifications = sections.some(({ items }) =>
    items.some(({ id }) => id === NavItemId.NOTIFICATIONS)
  );

  return (
    <>
      <Show if={isCustomizing}>
        <div
          onClick={stopCustomizing}
          className="fixed inset-0 z-[13] bg-alpha-100"
        />
      </Show>
      <aside
        className={cx(
          getAsideWidthClassName({ isCustomizing, isOpenFromMobile }),
          "group flex no-print transition-all top-0 absolute z-[14] h-screen flex-col border-r border-alpha-100 bg-white"
        )}
      >
        <div className="flex shrink-0">
          <AsideTop
            isOpenFromMobile={isOpenFromMobile}
            setIsOpenFromMobile={setIsOpenFromMobile}
          />
        </div>
        <div className="hide-scroll flex grow flex-col overflow-y-auto">
          <nav className="flex h-full flex-col relative">
            {!isCustomizing &&
            type.isMasterSearch &&
            (type.isBoomCRM || type.isBoomScreen || type.isRentReporting) ? (
              <div className="mb-1 px-4">
                <MasterSearch />
              </div>
            ) : null}
            <Show if={hasNotifications}>
              <NotificationToasts />
            </Show>
            {isCustomizing ? <CustomizeNavSections /> : <NavSections />}
          </nav>
        </div>
        <Show if={!isCustomizing}>
          <div className="flex shrink-0 flex-col justify-end">
            <Show if={type.isBoomProducts}>
              <ExploreProductsNavButton expanded={isOpenFromMobile} />
            </Show>
            <HiddenUpdatesNotice />
            <SidebarStatusBanner />

            <Menu />
          </div>
        </Show>
      </aside>
    </>
  );
};

export const Aside = (props: AsideProps) => {
  const { stopCustomizing } = useNavCustomization();

  useEffect(() => stopCustomizing, [stopCustomizing]);

  return <AsideContent {...props} />;
};
